import { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';

export const PHASE = {
  FOCUS: 'focus',
  SHORT: 'short',
  LONG:  'long',
};

export const PHASE_LABELS = {
  [PHASE.FOCUS]: 'Focus',
  [PHASE.SHORT]: 'Short break',
  [PHASE.LONG]:  'Long break',
};

const DEFAULTS = {
  [PHASE.FOCUS]: 25 * 60,
  [PHASE.SHORT]:  5 * 60,
  [PHASE.LONG]:  15 * 60,
};

const KEY = 'dayrit_pomodoro';

function load() {
  try { return JSON.parse(localStorage.getItem(KEY) || 'null'); } catch { return null; }
}
function save(data) {
  try { localStorage.setItem(KEY, JSON.stringify(data)); } catch {}
}

const PomodoroContext = createContext(null);

// ── Provider — mount once at app root ────────────────────────────────────
export function PomodoroProvider({ children }) {
  const saved = load();

  const [durations, setDurations] = useState({
    [PHASE.FOCUS]: saved?.durations?.[PHASE.FOCUS] ?? DEFAULTS[PHASE.FOCUS],
    [PHASE.SHORT]: saved?.durations?.[PHASE.SHORT] ?? DEFAULTS[PHASE.SHORT],
    [PHASE.LONG]:  saved?.durations?.[PHASE.LONG]  ?? DEFAULTS[PHASE.LONG],
  });
  const [phase,       setPhase]       = useState(PHASE.FOCUS);
  const [timeLeft,    setTimeLeft]    = useState(saved?.durations?.[PHASE.FOCUS] ?? DEFAULTS[PHASE.FOCUS]);
  const [running,     setRunning]     = useState(false);
  const [round,       setRound]       = useState(1);
  const [sessionDone, setSessionDone] = useState(0);
  const [sound,       setSound]       = useState(saved?.sound ?? true);

  // UI open/close state — also lives here so trigger + modal stay in sync
  const [open, setOpen] = useState(false);

  const tickRef = useRef(null);

  useEffect(() => {
    save({ durations, sound });
  }, [durations, sound]);

  const playDing = useCallback(() => {
    if (!sound) return;
    try {
      const ctx  = new (window.AudioContext || window.webkitAudioContext)();
      const now  = ctx.currentTime;
      const vol  = ctx.createGain();
      vol.gain.setValueAtTime(0, now);
      vol.connect(ctx.destination);

      // ── Helper: play one tone note ─────────────────────────────────
      const note = (freq, start, dur, gainPeak = 0.35, type = 'sine') => {
        const osc  = ctx.createOscillator();
        const gain = ctx.createGain();
        // Slight warmth: mix in a low sub-oscillator
        const sub  = ctx.createOscillator();
        const subG = ctx.createGain();
        sub.type = 'sine';
        sub.frequency.setValueAtTime(freq / 2, now + start);
        subG.gain.setValueAtTime(gainPeak * 0.15, now + start);
        subG.gain.exponentialRampToValueAtTime(0.001, now + start + dur);
        sub.connect(subG);
        subG.connect(ctx.destination);
        sub.start(now + start);
        sub.stop(now + start + dur + 0.05);

        osc.type = type;
        osc.frequency.setValueAtTime(freq, now + start);
        gain.gain.setValueAtTime(0.001, now + start);
        gain.gain.linearRampToValueAtTime(gainPeak, now + start + 0.02); // fast attack
        gain.gain.exponentialRampToValueAtTime(0.001, now + start + dur);
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start(now + start);
        osc.stop(now + start + dur + 0.05);
      };

      // ── Phase-aware completion chord ─────────────────────────────────
      // Each phase gets its own feel:
      //   Focus done → bright ascending major arpeggio (rewarding)
      //   Short break done → gentle two-tone chime (soft)
      //   Long break done → warm deep gong strike (satisfying)

      if (phase === PHASE.FOCUS) {
        // Bright ascending arpeggio: C5 → E5 → G5 → C6
        note(523.25, 0.00, 0.55, 0.30);  // C5
        note(659.25, 0.12, 0.50, 0.28);  // E5
        note(783.99, 0.24, 0.48, 0.26);  // G5
        note(1046.5, 0.36, 0.70, 0.22);  // C6  — sustained top note
        // Add a shimmer overtone on the last note
        note(2093.0, 0.36, 0.40, 0.06, 'sine'); // C7 shimmer
      } else if (phase === PHASE.SHORT) {
        // Soft two-note chime: G5 → C6
        note(783.99, 0.00, 0.60, 0.22);  // G5
        note(1046.5, 0.18, 0.80, 0.18);  // C6
      } else {
        // Long break — warm bowl gong: low C3 strike with long decay
        note(130.81, 0.00, 1.80, 0.40);  // C3 fundamental
        note(261.63, 0.00, 1.40, 0.20);  // C4 overtone
        note(392.00, 0.02, 1.00, 0.12);  // G4 fifth
        note(523.25, 0.04, 0.70, 0.08);  // C5 high overtone
      }

    } catch {}
  }, [sound, phase]);

  const handlePhaseComplete = useCallback(() => {
    setRunning(false);
    playDing();
    setPhase(prev => {
      if (prev === PHASE.FOCUS) {
        setSessionDone(d => d + 1);
        setRound(r => {
          const nextPhase = (r % 4 === 0) ? PHASE.LONG : PHASE.SHORT;
          setTimeLeft(durations[nextPhase]);
          setPhase(nextPhase);
          return r + 1;
        });
        return prev;
      } else {
        setTimeLeft(durations[PHASE.FOCUS]);
        return PHASE.FOCUS;
      }
    });
  }, [durations, playDing]);

  // Core tick — lives in context, never unmounts with sidebar
  useEffect(() => {
    if (!running) { clearInterval(tickRef.current); return; }
    tickRef.current = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) { clearInterval(tickRef.current); handlePhaseComplete(); return 0; }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(tickRef.current);
  }, [running, phase, handlePhaseComplete]);

  // Tab title
  const total   = durations[phase];
  const pct     = total > 0 ? Math.round(((total - timeLeft) / total) * 100) : 0;
  const minutes = Math.floor(timeLeft / 60);
  const seconds = timeLeft % 60;
  const display = `${String(minutes).padStart(2,'0')}:${String(seconds).padStart(2,'0')}`;

  useEffect(() => {
    document.title = running ? `${display} · ${PHASE_LABELS[phase]} — Dayrit` : 'Dayrit';
    return () => { document.title = 'Dayrit'; };
  }, [display, running, phase]);

  const start  = useCallback(() => setRunning(true),  []);
  const pause  = useCallback(() => setRunning(false), []);
  const reset  = useCallback(() => { setRunning(false); setTimeLeft(durations[phase]); }, [phase, durations]);
  const skip   = useCallback(() => { setRunning(false); handlePhaseComplete(); }, [handlePhaseComplete]);

  const switchPhase = useCallback((p) => {
    setRunning(false); setPhase(p); setTimeLeft(durations[p]);
  }, [durations]);

  const updateDuration = useCallback((p, minutes) => {
    const secs = Math.max(1, Math.min(99, minutes)) * 60;
    setDurations(prev => ({ ...prev, [p]: secs }));
    setPhase(cur => { if (cur === p) setTimeLeft(secs); return cur; });
  }, []);

  const value = {
    phase, timeLeft, running, round, sessionDone, sound, durations,
    display, pct, total, open,
    start, pause, reset, skip, switchPhase, updateDuration, setSound,
    setOpen,
  };

  return (
    <PomodoroContext.Provider value={value}>
      {children}
    </PomodoroContext.Provider>
  );
}

export function usePomodoro() {
  const ctx = useContext(PomodoroContext);
  if (!ctx) throw new Error('usePomodoro must be used inside PomodoroProvider');
  return ctx;
}